import React, { useState, useEffect, useRef } from 'react';
import { Volume2, Sparkles, RefreshCw } from 'lucide-react';
import { speakLetter, playTickSound, playSuccessSound } from '../../utils/stopGameAudio';
import { AVAILABLE_STOP_LETTERS } from '../../services/stopGameRoomService';

interface StopLetterWheelProps {
  letter: string;
  isSpinning?: boolean;
  onSpinComplete?: () => void;
  canReroll?: boolean;
  onReroll?: () => void;
  size?: 'sm' | 'md' | 'lg';
  durationMs?: number;
}

export const StopLetterWheel: React.FC<StopLetterWheelProps> = ({
  letter,
  isSpinning = false,
  onSpinComplete,
  canReroll = false,
  onReroll,
  size = 'lg',
  durationMs = 3000
}) => {
  const [displayLetter, setDisplayLetter] = useState(letter || '?');
  const [animating, setAnimating] = useState(false);
  const onSpinCompleteRef = useRef(onSpinComplete);

  useEffect(() => {
    onSpinCompleteRef.current = onSpinComplete;
  }, [onSpinComplete]);

  useEffect(() => {
    if (isSpinning) {
      setAnimating(true);
      const startTime = Date.now();
      let lastTickSound = 0;

      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(1, elapsed / durationMs);

        // Pick random letter during reel spin
        const randLetter = AVAILABLE_STOP_LETTERS[Math.floor(Math.random() * AVAILABLE_STOP_LETTERS.length)];
        setDisplayLetter(randLetter);

        // Play tick sound every ~180ms
        if (Date.now() - lastTickSound > 180) {
          playTickSound();
          lastTickSound = Date.now();
        }

        if (progress >= 1) {
          clearInterval(interval);
          setDisplayLetter(letter);
          setAnimating(false);
          playSuccessSound();
          speakLetter(letter);
          onSpinCompleteRef.current?.();
        }
      }, 60);

      return () => clearInterval(interval);
    } else {
      setDisplayLetter(letter || '?');
      setAnimating(false);
    }
  }, [letter, isSpinning, durationMs]);

  const sizeClasses = {
    sm: 'w-16 h-16 text-3xl',
    md: 'w-24 h-24 text-5xl',
    lg: 'w-32 h-32 md:w-40 md:h-40 text-6xl md:text-8xl'
  };

  return (
    <div className="flex flex-col items-center justify-center my-4">
      <div className="relative group">
        <div
          className={`${sizeClasses[size]} rounded-3xl bg-gradient-to-tr from-indigo-600 via-purple-600 to-pink-500 text-white font-black flex items-center justify-center shadow-xl shadow-purple-500/30 transform transition-all duration-300 ${
            animating
              ? 'scale-110 rotate-3 ring-8 ring-purple-300/60 animate-pulse'
              : 'hover:scale-105'
          } border-4 border-white/80`}
        >
          <span>{displayLetter}</span>
        </div>

        {letter && !animating && (
          <button
            onClick={() => speakLetter(letter)}
            className="absolute -bottom-2 -right-2 p-2 bg-white text-indigo-600 rounded-full shadow-md hover:bg-indigo-50 hover:scale-110 transition-all border border-indigo-100"
            title="Hear pronunciation"
            aria-label={`Pronounce letter ${letter}`}
          >
            <Volume2 className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="mt-3 flex items-center space-x-2">
        <span
          className={`text-sm font-semibold uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1.5 transition-colors ${
            animating
              ? 'text-pink-800 bg-pink-100 animate-pulse'
              : 'text-purple-700 bg-purple-100'
          }`}
        >
          <Sparkles className="w-3.5 h-3.5" />
          {animating ? (
            <span>Choosing Letter... 🎲</span>
          ) : (
            <span>
              Target Letter: <strong className="text-base text-purple-900">{letter || '—'}</strong>
            </span>
          )}
        </span>

        {canReroll && onReroll && !animating && (
          <button
            onClick={onReroll}
            className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-gray-100 rounded-full transition-colors"
            title="Reroll letter"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default StopLetterWheel;
